import { useState, useEffect, useRef, useCallback } from "react";
import { ArrowLeft, Phone, Video, Send, Mic, Paperclip, ShieldBan } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { MessageBubble } from "./MessageBubble";
import { TypingIndicator } from "./TypingIndicator";
import { UserProfileSheet } from "./UserProfileSheet";
import { CallView } from "./CallView";
import { IncomingCallOverlay } from "./IncomingCallOverlay";
import { VoiceRecorder } from "./VoiceRecorder";
import type { Chat, Message } from "@/hooks/useMessaging";
import { useToast } from "@/hooks/use-toast";

interface ChatViewProps {
  chat: Chat;
  currentUserId: string | null;
  displayName: string;
  avatarUrl: string | null;
  onBack: () => void;
  onSendMessage: (chatId: string, content: string, type?: string, mediaUrl?: string) => Promise<void>;
  onFetchMessages: (chatId: string) => Promise<Message[]>;
  onMarkAsRead: (chatId: string) => Promise<void>;
  onUploadMedia: (file: File, chatId: string) => Promise<string | null>;
  autoAnswer?: "audio" | "video" | null;
}

export const ChatView = ({
  chat, currentUserId, displayName, avatarUrl, onBack,
  onSendMessage, onFetchMessages, onMarkAsRead, onUploadMedia, autoAnswer,
}: ChatViewProps) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [isOtherTyping, setIsOtherTyping] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [activeCall, setActiveCall] = useState<{ isVideo: boolean; isIncoming: boolean } | null>(null);
  const [incomingCall, setIncomingCall] = useState<{ isVideo: boolean } | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [isBlockedByOther, setIsBlockedByOther] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { toast } = useToast();

  const otherParticipant = chat.participants?.find(p => p.user_id !== currentUserId) || null;
  const otherUserId = otherParticipant?.user_id;

  // Check block status
  useEffect(() => {
    if (!currentUserId || !otherUserId || chat.is_group) return;
    const checkBlock = async () => {
      const [{ data: iBlocked }, { data: theyBlocked }] = await Promise.all([
        supabase.from("blocked_users").select("id").eq("user_id", currentUserId).eq("blocked_user_id", otherUserId).maybeSingle(),
        supabase.from("blocked_users").select("id").eq("user_id", otherUserId).eq("blocked_user_id", currentUserId).maybeSingle(),
      ]);
      setIsBlocked(!!iBlocked);
      setIsBlockedByOther(!!theyBlocked);
    };
    checkBlock();
  }, [currentUserId, otherUserId, chat.is_group]);

  const chatBlocked = isBlocked || isBlockedByOther;

  const loadMessages = useCallback(async () => {
    const msgs = await onFetchMessages(chat.id);
    setMessages(msgs);
    onMarkAsRead(chat.id);
  }, [chat.id, onFetchMessages, onMarkAsRead]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  // Auto-answer call from notification
  useEffect(() => {
    if (autoAnswer) {
      setActiveCall({ isVideo: autoAnswer === "video", isIncoming: true });
    }
  }, [autoAnswer]);

  // Realtime messages
  useEffect(() => {
    const channel = supabase
      .channel(`chat-${chat.id}`)
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "messages",
        filter: `chat_id=eq.${chat.id}`,
      }, (payload) => {
        setMessages(prev => [...prev, payload.new as Message]);
        onMarkAsRead(chat.id);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [chat.id, onMarkAsRead]);

  // Typing indicator via broadcast
  useEffect(() => {
    const channel = supabase
      .channel(`typing-${chat.id}`)
      .on("broadcast", { event: "typing" }, (payload) => {
        if (payload.payload?.user_id !== currentUserId) {
          setIsOtherTyping(true);
          if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = setTimeout(() => setIsOtherTyping(false), 2500);
        }
      })
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, [chat.id, currentUserId]);

  // Listen for incoming calls
  useEffect(() => {
    const channel = supabase
      .channel(`call-signal-${chat.id}`)
      .on("broadcast", { event: "incoming-call" }, (msg) => {
        if (msg.payload?.from !== currentUserId) {
          setIncomingCall({ isVideo: msg.payload?.isVideo || false });
        }
      })
      .on("broadcast", { event: "end-call" }, () => {
        setIncomingCall(null);
        setActiveCall(null);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [chat.id, currentUserId]);

  // Auto scroll
  useEffect(() => {
    setTimeout(() => {
      scrollRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  }, [messages.length, isOtherTyping]);

  const broadcastTyping = useCallback(() => {
    supabase.channel(`typing-${chat.id}`).send({
      type: "broadcast",
      event: "typing",
      payload: { user_id: currentUserId },
    });
  }, [chat.id, currentUserId]);

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setText(e.target.value);
    broadcastTyping();
  };

  const handleSend = async () => {
    if (!text.trim() || sending || chatBlocked) return;
    if (chatBlocked) {
      toast({ title: "Utente bloccato", description: "Non puoi inviare messaggi.", variant: "destructive" });
      return;
    }
    setSending(true);
    await onSendMessage(chat.id, text.trim());
    setText("");
    setSending(false);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || chatBlocked) return;

    const isImage = file.type.startsWith("image/");
    const isAudio = file.type.startsWith("audio/");
    const isVideo = file.type.startsWith("video/");
    const type = isImage ? "image" : isVideo ? "video" : isAudio ? "audio" : "file";

    const url = await onUploadMedia(file, chat.id);
    if (url) {
      await onSendMessage(chat.id, isImage || isVideo ? "" : file.name, type, url);
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleVoiceNote = () => {
    setIsRecording(true);
  };

  const handleVoiceSend = async (blob: Blob) => {
    setIsRecording(false);
    const file = new File([blob], `voice-${Date.now()}.webm`, { type: "audio/webm" });
    const url = await onUploadMedia(file, chat.id);
    if (url) {
      await onSendMessage(chat.id, "", "audio", url);
    }
  };

  const handleCall = (video: boolean) => {
    if (chatBlocked) {
      toast({ title: "Utente bloccato", description: "Non puoi effettuare chiamate.", variant: "destructive" });
      return;
    }
    setActiveCall({ isVideo: video, isIncoming: false });
  };

  const handleAcceptCall = () => {
    if (incomingCall) {
      setActiveCall({ isVideo: incomingCall.isVideo, isIncoming: true });
      setIncomingCall(null);
    }
  };

  const handleDeclineCall = () => {
    setIncomingCall(null);
    // Broadcast end
    supabase.channel(`call-${chat.id}`).send({
      type: "broadcast",
      event: "end-call",
      payload: { from: currentUserId },
    });
  };

  return (
    <motion.div
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "100%" }}
      transition={{ type: "spring", damping: 25, stiffness: 200 }}
      className="flex flex-col h-full bg-background/60 backdrop-blur-2xl"
    >
      {/* Header */}
      <div
        className="flex items-center gap-3 px-3 py-3 border-b border-border/30 flex-shrink-0"
        style={{ backdropFilter: "blur(20px)", background: "hsl(var(--background) / 0.7)" }}
      >
        <button onClick={onBack} className="p-1.5 rounded-full hover:bg-muted/40 transition-colors active:scale-90">
          <ArrowLeft className="w-5 h-5 text-primary" />
        </button>
        <button
          onClick={() => setProfileOpen(true)}
          className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80 transition-opacity active:scale-[0.98]"
        >
          <Avatar className="w-9 h-9">
            <AvatarImage src={avatarUrl || undefined} />
            <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
              {displayName.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0 text-left">
            <p className="text-[15px] font-semibold truncate">{displayName}</p>
            {chat.is_group ? (
              <p className="text-[11px] text-muted-foreground truncate">
                {chat.participants?.length || 0} partecipanti
              </p>
            ) : isOtherTyping ? (
              <p className="text-[11px] text-primary font-medium">sta scrivendo...</p>
            ) : null}
          </div>
        </button>
        <button onClick={() => handleCall(false)} className="p-2 rounded-full hover:bg-muted/40 transition-colors">
          <Phone className="w-5 h-5 text-primary" />
        </button>
        <button onClick={() => handleCall(true)} className="p-2 rounded-full hover:bg-muted/40 transition-colors">
          <Video className="w-5 h-5 text-primary" />
        </button>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1">
        <div className="py-4 min-h-full flex flex-col justify-end">
          {messages.map(msg => (
            <MessageBubble key={msg.id} message={msg} isMine={msg.sender_id === currentUserId} />
          ))}
          {isOtherTyping && <TypingIndicator />}
          <div ref={scrollRef} />
        </div>
      </ScrollArea>

      {/* Input */}
      <AnimatePresence mode="wait">
        {chatBlocked ? (
          <div
            key="blocked-bar"
            className="flex items-center justify-center gap-2 px-3 py-4 border-t border-border/30 flex-shrink-0"
            style={{ backdropFilter: "blur(20px)", background: "hsl(var(--background) / 0.7)" }}
          >
            <ShieldBan className="w-5 h-5 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {isBlocked ? "Hai bloccato questo utente" : "Questo utente ti ha bloccato"}
            </p>
          </div>
        ) : isRecording ? (
          <VoiceRecorder
            key="voice-recorder"
            onSend={handleVoiceSend}
            onCancel={() => setIsRecording(false)}
          />
        ) : (
          <div
            key="text-input"
            className="flex items-center gap-2 px-3 py-2.5 border-t border-border/30 flex-shrink-0"
            style={{ backdropFilter: "blur(20px)", background: "hsl(var(--background) / 0.7)" }}
          >
            <button onClick={() => fileRef.current?.click()} className="p-2 rounded-full hover:bg-muted/40 transition-colors">
              <Paperclip className="w-5 h-5 text-muted-foreground" />
            </button>
            <input ref={fileRef} type="file" className="hidden" accept="image/*,audio/*,video/*" onChange={handleFileUpload} />
            <div className="flex-1 relative">
              <Input
                value={text}
                onChange={handleTextChange}
                onKeyDown={e => e.key === "Enter" && !e.shiftKey && handleSend()}
                placeholder="Messaggio..."
                className="bg-muted/40 backdrop-blur-sm border-none rounded-full h-10 pr-10"
              />
            </div>
            {text.trim() ? (
              <button
                onClick={handleSend}
                disabled={sending}
                className="p-2.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-all active:scale-90"
              >
                <Send className="w-4 h-4" />
              </button>
            ) : (
              <button onClick={handleVoiceNote} className="p-2.5 rounded-full hover:bg-muted/40 transition-colors">
                <Mic className="w-5 h-5 text-muted-foreground" />
              </button>
            )}
          </div>
        )}
      </AnimatePresence>

      {/* Profile Sheet */}
      <UserProfileSheet
        open={profileOpen}
        onOpenChange={setProfileOpen}
        participant={otherParticipant}
        currentUserId={currentUserId}
      />

      {/* Call Views */}
      <AnimatePresence>
        {incomingCall && !activeCall && (
          <IncomingCallOverlay
            displayName={displayName}
            avatarUrl={avatarUrl}
            isVideo={incomingCall.isVideo}
            onAccept={handleAcceptCall}
            onDecline={handleDeclineCall}
          />
        )}
      </AnimatePresence>

      {activeCall && currentUserId && (
        <CallView
          chatId={chat.id}
          currentUserId={currentUserId}
          isVideo={activeCall.isVideo}
          displayName={displayName}
          avatarUrl={avatarUrl}
          isIncoming={activeCall.isIncoming}
          onClose={() => setActiveCall(null)}
        />
      )}
    </motion.div>
  );
};
